import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, output, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CarTrackSettings } from '../../models/car-track-settings';
import { SuspensionSettings } from '../../models/suspension-settings';
import { DrivetrainSettings } from '../../models/drivetrain-settings';
import { Suspension } from '../../models/Suspension';
import { Drivetrain } from '../../models/calculatedSettings';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  advice?: TuningAdvice;
}

interface TuningAdvice {
  needsMoreInfo: boolean;
  questions: string[];
  summary: string;
  recommendations: TuningRecommendation[];
  notes?: string;
}

interface TuningRecommendation {
  system: string;
  component: string;
  current: string;
  target: string;
  delta: string;
  reason: string;
  effects: string;
  risk: string;
  confidence: number;
}

@Component({
  selector: 'app-gt7-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <!-- Bottom Dock -->
    <!--<div class="bottom-dock">
      <button class="dock-button" (click)="toggleExpanded()">
        <i class="fa-solid" [class.fa-chevron-up]="isExpanded" [class.fa-chevron-down]="!isExpanded"></i>
      </button>
      <button class="dock-button dock-button-wide" (click)="toggleExpanded()">
        GT7 Race Engineer
      </button>
    </div> -->

    <!-- Chat Panel (pops up above dock) -->
    <div class="chat-panel" *ngIf="isExpanded">
      <div class="chat-header d-flex justify-content-bewteen">
        <i class="fa-solid fa-comments me-2"></i>
        <span>GT7 Race Engineer</span>
        <button class="btn-close btn-close-white ms-auto" (click)="toggleExpanded()"></button>
      </div>

      <!-- Chat Body -->
      <div class="chat-body">
        <!-- Status -->
        <div class="status-bar" [ngClass]="{'bg-success': isConnected, 'bg-warning': !isConnected}" [hidden]="!isSubscribed">
          <small>{{ isConnected ? 'AI Ready' : 'Connecting...' }}</small>
        </div>

        <!-- Messages -->
        <div class="messages" #messagesContainer>
          <div *ngFor="let message of messages" class="message" [ngClass]="message.role">
            <div class="message-content">
              <!-- Only show text if there's no structured advice -->
              <div class="text" *ngIf="!message.advice || (message.advice.needsMoreInfo && !message.advice.recommendations?.length)" [innerHtml]="message.content"></div>

              <!-- AI Questions -->
              <div *ngIf="message.advice?.needsMoreInfo && message.advice.questions.length > 0" class="questions mt-2">
                <div class="questions-summary">
                  <strong>I need more information:</strong>
                </div>
                <ul class="question-list">
                  <li *ngFor="let question of message.advice.questions">{{ question }}</li>
                </ul>
              </div>

              <!-- AI Recommendations -->
              <div *ngIf="message.advice && !message.advice.needsMoreInfo && message.advice.recommendations" class="recommendations mt-2">
                <div class="recommendation-summary">
                  <strong>{{ message.advice.summary }}</strong>
                </div>
                <div *ngFor="let rec of message.advice.recommendations" class="recommendation-item">
                  <div class="rec-header">
                    <span class="component">{{ rec.component }}</span>
                    <span class="confidence">{{ (rec.confidence * 100) | number:'1.0-0' }}%</span>
                  </div>
                  <div class="rec-change">
                    {{ rec.current }} → {{ rec.target }} ({{ rec.delta }})
                  </div>
                  <div class="rec-reason">{{ rec.reason }}</div>
                </div>
                <div *ngIf="message.advice.notes" class="notes">
                  <small><strong>Notes:</strong> {{ message.advice.notes }}</small>
                </div>
              </div>
            </div>
            <div class="timestamp">
              {{ message.timestamp | date:'HH:mm' }}
            </div>
          </div>
        </div>

        <!-- Quick Prompts -->
        <div class="quick-prompts" *ngIf="quickPrompts.length > 0">
          <button
            *ngFor="let prompt of quickPrompts.slice(0, 3)"
            class="btn btn-sm btn-outline-secondary mb-1 me-1"
            (click)="sendMessage(prompt)">
            {{ prompt }}
          </button>
        </div>

        <!-- Input -->
        <div class="chat-input">
          <div class="input-group">
            <input
              type="text"
              class="form-control"
              [(ngModel)]="newMessage"
              (keyup.enter)="sendMessage()"
              placeholder="Ask about your setup..."
              [disabled]="isLoading">
            <button
              class="btn btn-primary"
              (click)="sendMessage()"
              [disabled]="isLoading || !newMessage.trim() || !isSubscribed">
              <i class="fa-solid" [class.fa-paper-plane]="!isLoading" [class.fa-spinner]="isLoading" [class.fa-spin]="isLoading"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`

    


    .chat-panel {
      z-index: 20;
      position: fixed;
      bottom: 172px;
      right: 20px;
      background: white;
      border: 1px solid #ddd;
      border-radius: 10px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transition: all 0.3s ease;
      width: 345px;
      height: 600px;
      max-width: 88%;
    }

    @media (min-width: 768px) {
      .chat-panel{
        bottom: 100px;
        width: 400px;
      }
    }

    .chat-widget.expanded {
      display: block;
      width: 350px;
      height: 500px;
    }

    //.chat-header{
    //  display: none;
    //}

    .chat-header {
      background: #007bff;
      color: white;
      padding: 12px;
      border-radius: 10px 10px 0 0;
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
    }

    //.chat-header:hover {
    //  background: #0056b3;
    //}

    .chat-body {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .status-bar {
      padding: 4px 8px;
      text-align: center;
      color: white;
      font-size: 0.75rem;
    }

    .messages {
      flex: 1;
      overflow-y: auto;
      padding: 10px;
      max-height: 340px;
    }

    .message {
      margin-bottom: 12px;
    }

    .message.user {
      text-align: right;
    }

    .message.user .message-content {
      background: #007bff;
      color: white;
      margin-left: 30px;
    }

    .message.assistant .message-content {
      background: #f8f9fa;
      color: #333;
      margin-right: 30px;
    }

    .message-content {
      padding: 8px 12px;
      border-radius: 12px;
      display: inline-block;
      max-width: 85%;
    }

    .timestamp {
      font-size: 0.7rem;
      color: #666;
      margin-top: 2px;
    }

    .recommendations {
      background: rgba(255,255,255,0.9);
      border-radius: 6px;
      padding: 8px;
      margin-top: 8px;
    }

    .recommendation-summary {
      color: #007bff;
      margin-bottom: 6px;
      font-size: 0.9rem;
    }

    .recommendation-item {
      background: rgba(0,123,255,0.1);
      border-radius: 4px;
      padding: 6px;
      margin-bottom: 4px;
      font-size: 0.8rem;
    }

    .rec-header {
      display: flex;
      justify-content: space-between;
      font-weight: bold;
    }

    .confidence {
      color: #28a745;
    }

    .rec-change {
      color: #007bff;
      font-weight: 500;
    }

    .rec-reason {
      color: #666;
      font-size: 0.75rem;
    }

    .notes {
      margin-top: 6px;
      color: #666;
    }

    .quick-prompts {
      padding: 8px 10px 4px;
      border-top: 1px solid #eee;
    }

    .chat-input {
      padding: 10px;
      border-top: 1px solid #eee;
    }

    ::-webkit-scrollbar {
      width: 4px;
    }

    ::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    ::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 2px;
    }

    .bottom-dock {
      position: fixed;
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 8px;
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(10px);
      border-radius: 16px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.15);
      z-index: 1000;
    }

    .dock-button {
      background: rgba(100, 100, 100, 0.6);
      border: none;
      color: white;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 18px;
    }

    .dock-button:hover {
      background: rgba(100, 100, 100, 0.8);
      transform: translateY(-2px);
    }

    .dock-button-wide {
      width: auto;
      padding: 0 20px;
      border-radius: 24px;
      font-size: 14px;
      font-weight: 500;
    }

    //.chat-panel {
    //  position: fixed;
    //  bottom: 80px;
    //  left: 50%;
    //  transform: translateX(-50%);
    //  width: 400px;
    //  height: 500px;
    //  background: white;
    //  border-radius: 12px;
    //  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
    //  z-index: 999;
    //  display: flex;
    //  flex-direction: column;
    //}

    .questions {
      background: rgba(255,193,7,0.2);
      border-radius: 6px;
      padding: 8px;
      margin-top: 8px;
    }

    .questions-summary {
      color: #856404;
      margin-bottom: 6px;
      font-size: 0.9rem;
    }

    .question-list {
      margin: 4px 0 0 0;
      padding-left: 20px;
      font-size: 0.8rem;
      color: #666;
    }
  `]
})
export class GT7ChatWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  
  @Input() carSetupState: any = {};
  @Input() isExpanded = false;
  @Input() carTrackSettings: CarTrackSettings;
  @Input() suspensionSettings: SuspensionSettings;
  @Input() drivetrainSettings: DrivetrainSettings;
  @Input() suspension: Suspension;
  @Input() drivetrain: Drivetrain;
  @Input() savedSettingsName: string;
  @Input() isSubscribed: boolean;

  @Output() isExpandedChange = new EventEmitter<boolean>();

  isConnected = false;
  isLoading = false;
  
  messages: ChatMessage[] = [];
  newMessage = '';
  quickPrompts: string[] = [];
  
  private carId = 'current-setup';
  private setupId = 'live-setup';
  
  constructor(private http: HttpClient) {}
  
  ngOnInit() {
    if (this.isSubscribed) {
      this.checkConnection();
      this.loadQuickPrompts();
    }
    this.initializeChat();
  }
  
  ngOnDestroy() {
    // Cleanup if needed
  }
  
  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    this.isExpandedChange.emit(this.isExpanded);
    if (this.isExpanded) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }
  
  async checkConnection() {
    try {
      const response = await this.http.get(`${environment.protuneApiUrl}/gt7/status`).toPromise();
      this.isConnected = true;
    } catch (error) {
      this.isConnected = false;
      console.warn('GT7 service not available:', error);
    }
  }
  
  async loadQuickPrompts() {
    try {
      const prompts = await this.http.post<string[]>(`${environment.protuneApiUrl}/gt7/quick-prompts`, {
        carSetupState: this.getCurrentSetupState()
      }).toPromise();
      
      this.quickPrompts = prompts?.slice(0, 6) || [
        'How can I improve lap times?',
        'Reduce understeer',
        'Car feels unstable'
      ];
    } catch (error) {
      this.quickPrompts = [
        'How can I improve lap times?',
        'Reduce understeer', 
        'Car feels unstable'
      ];
    }
  }
  
  initializeChat() {
    if (this.isSubscribed) {
      this.messages = [
        {
          role: 'assistant',
          content: 'Hi! I\'m your GT7 Race Engineer. I can analyze your current setup and provide tuning advice. What would you like to improve?',
          timestamp: new Date()
        }
      ];
    } else {
      this.messages = [
        {
          role: 'assistant',
          content: 'It looks you are not subscribed to AI Engineer. Please subscribe to use this feature <a href="/subscription" class="text-primary">here</a>. ',
          timestamp: new Date()
        }
      ];
    }
  }
  
  async sendMessage(message?: string) {
    const messageText = message || this.newMessage.trim();
    if (!messageText) return;
    
    // Add user message
    this.messages.push({
      role: 'user',
      content: messageText,
      timestamp: new Date()
    });
    
    this.newMessage = '';
    this.isLoading = true;
    
    setTimeout(() => this.scrollToBottom(), 50);
    
    try {
      if (this.suspension == null || this.drivetrain == null) {
        this.messages.push({
          role: 'assistant',
          content: 'It appears you haven\'t calculated your suspension or transmission yet. I can still assist you but my suggestions may be less accurate.',
          timestamp: new Date()
        })
      }

      const carSetupState = this.buildCarSetupState();
      
      // Debug: log what we're sending to help troubleshoot
      console.log('Sending to GT7 AI:', {
        message: messageText,
        gearset: carSetupState.gearset,
        camber: `${carSetupState.camberF}/${carSetupState.camberR}`,
        arb: `${carSetupState.arbF}/${carSetupState.arbR}`,
        carName: carSetupState.carName
      });
      
      const response = await this.http.post<any>(`${environment.protuneApiUrl}/gt7/chat`, {
        userId: 'current-user',
        carId: this.carId,
        setupId: this.setupId,
        message: messageText,
        carSetupState: carSetupState,
        forceStructured: true,
        
        // Add explicit context about what kind of advice is needed
        messageContext: {
          isGearingQuestion: messageText.toLowerCase().includes('gear') || 
                           messageText.toLowerCase().includes('transmission') || 
                           messageText.toLowerCase().includes('wheelspin'),
          isTransmissionFocused: messageText.toLowerCase().includes('gear') ||
                               messageText.toLowerCase().includes('ratio') ||
                               messageText.toLowerCase().includes('shift') ||
                               messageText.toLowerCase().includes('wheel'),
          isSuspensionQuestion: messageText.toLowerCase().includes('bounce') ||
                              messageText.toLowerCase().includes('jump') ||
                              messageText.toLowerCase().includes('curb') ||
                              messageText.toLowerCase().includes('kerb') ||
                              messageText.toLowerCase().includes('stiff') ||
                              messageText.toLowerCase().includes('soft') ||
                              messageText.toLowerCase().includes('ride') ||
                              messageText.toLowerCase().includes('bump'),
          questionType: this.identifyQuestionType(messageText)
        }
      }).toPromise();
      
      // Add AI response
      this.messages.push({
        role: 'assistant',
        content: response.text,
        advice: response.advice,
        timestamp: new Date()
      });
      
    } catch (error) {
      console.error('Error sending message:', error);
      this.messages.push({
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or check if the GT7 service is running.',
        timestamp: new Date()
      });
    } finally {
      this.isLoading = false;
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }
  
  private buildCarSetupState() {
    //const carSetupState = this.getCurrentSetupState();
    //// Extract calculated results from the tuning calculator
    //const suspension = carSetupState?.suspension || {};
    //const drivetrain = carSetupState?.drivetrain || {};
    //const drivetrainSettings = carSetupState?.drivetrainSettings || {};

    // Build flat structure matching C# CarSetupState model
    return {
      // Basic Info
      carId: this.carTrackSettings?.vehicle?.id?.toString() || 'unknown',
      carName: this.getCarName(),
      patch: '1.49',
      track: this.carTrackSettings.track.name,
      tyres: this.getTyreCompound(),

      // Aerodynamics - from suspension output
      aeroF: this.suspensionSettings.downforceFront || 0,
      aeroR: this.suspensionSettings.downforceRear || this.suspension.downforceRear || 0,

      // Suspension Natural Frequencies - calculated from spring rates
      naturalFrequencyFront: this.suspension?.springRateFront || 0,
      naturalFrequencyRear: this.suspension?.springRateRear || 0,

      // Damping - from suspension output (Extension = Rebound in GT7 terminology)
      dampFComp: this.suspension?.damperCompressionFront || 0,
      dampFReb: this.suspension?.damperExtensionFront || 0,
      dampRComp: this.suspension?.damperCompressionRear || 0,
      dampRReb: this.suspension?.damperExtensionRear || 0,

      // Anti-Roll Bars - from suspension output
      arbF: this.suspension?.antiRollBarFront || 0,
      arbR: this.suspension?.antiRollBarRear || 0,

      // Alignment - from suspension output
      camberF: this.suspension?.camberAngleFront || 0,
      camberR: this.suspension?.camberAngleRear || 0,
      toeF: this.suspension?.toeAngleFront || 0,
      toeR: this.suspension?.toeAngleRear || 0,

      // LSD - from drivetrain output (use rear values for RWD/AWD, front for FWD)
      lsdInit: this.drivetrain?.initialTorqueRear || this.drivetrain?.initialTorqueFront || this.drivetrainSettings.lsdSetting || 0,
      lsdAccel: this.drivetrain?.accelerationRear || this.drivetrain?.accelerationFront || 0,
      lsdBrake: this.drivetrain?.decelerationRear || this.drivetrain?.decelerationFront || 0,

      // Brakes - use default 0 for brake bias (frontal bias percentage)
      brakeBias: 0,

      // Ride Height - from suspension input (user settings)
      rideF: this.suspensionSettings?.rideHeightFront || 0,
      rideR: this.suspensionSettings?.rideHeightRear || 0,

      // Gearset - from drivetrain output (prefer calculated values, fallback to settings)
      gearset: {
        final: this.drivetrain?.finalGear || this.drivetrainSettings.finalGear || 0,
        ratios: [
          this.drivetrain?.gears?.[1]?.gearRatio || this.drivetrainSettings.gearRatios?.['1'] || 0,
          this.drivetrain?.gears?.[2]?.gearRatio || this.drivetrainSettings.gearRatios?.['2'] || 0,
          this.drivetrain?.gears?.[3]?.gearRatio || this.drivetrainSettings.gearRatios?.['3'] || 0,
          this.drivetrain?.gears?.[4]?.gearRatio || this.drivetrainSettings.gearRatios?.['4'] || 0,
          this.drivetrain?.gears?.[5]?.gearRatio || this.drivetrainSettings.gearRatios?.['5'] || 0,
          this.drivetrain?.gears?.[6]?.gearRatio || this.drivetrainSettings.gearRatios?.['6'] || 0,
          this.drivetrain?.gears?.[7]?.gearRatio || this.drivetrainSettings.gearRatios?.['7'] || 0,
          this.drivetrain?.gears?.[8]?.gearRatio || this.drivetrainSettings.gearRatios?.['8'] || 0
        ].filter(r => r > 0), // Remove zeros
        rpmRedline: this.drivetrainSettings.maxRPM || 0
      },

      // Ballast & ECU - defaults for now
      ballastKg: 0,
      ballastPos: 0,
      ecuPct: 100
    };
  }
  
  private getCarName(): string {
    const vehicle = this.carTrackSettings?.vehicle;
    if (vehicle) {
      return `${vehicle.manufacturer} ${vehicle.model}`;
    }
    return 'Unknown Vehicle';
  }
  
  private getTyreCompound(): string {
    const frontTire = this.carTrackSettings?.frontTires;
    if (frontTire === undefined) return 'SM';
    
    const tireMap: { [key: number]: string } = {
      [-4]: 'CH',
      [-3]: 'CM', 
      [-2]: 'CS',
      [-1]: 'SH',
      [0]: 'SM',
      [1]: 'SS',
      [2]: 'RH',
      [3]: 'RM',
      [4]: 'RS'
    };
    
    return tireMap[frontTire] || 'SM';
  }
  
  private identifyQuestionType(message: string): string {
    const lower = message.toLowerCase();
    
    // Transmission/Gearing issues
    if (lower.includes('gear') || lower.includes('transmission') || lower.includes('wheelspin') || 
        lower.includes('ratio') || lower.includes('shift') || lower.includes('launch')) {
      return 'transmission';
    }
    
    // Suspension issues  
    if (lower.includes('bounce') || lower.includes('jump') || lower.includes('curb') || lower.includes('kerb') ||
        lower.includes('stiff') || lower.includes('soft') || lower.includes('ride') || lower.includes('bump') ||
        lower.includes('spring') || lower.includes('damper')) {
      return 'suspension';
    }
    
    // Handling issues
    if (lower.includes('understeer') || lower.includes('oversteer') || lower.includes('turn') ||
        lower.includes('corner') || lower.includes('grip') || lower.includes('slide')) {
      return 'handling';
    }
    
    // Aerodynamic issues
    if (lower.includes('downforce') || lower.includes('aero') || lower.includes('drag') || 
        lower.includes('straight') || lower.includes('top speed')) {
      return 'aerodynamics';
    }
    
    // Brake issues
    if (lower.includes('brake') || lower.includes('stop') || lower.includes('lock')) {
      return 'braking';
    }
    
    return 'general';
  }

  private scrollToBottom() {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  getCurrentSetupState(): any {
    return {
      vehicle: this.carTrackSettings.vehicle,
      weight: this.carTrackSettings.weight,
      trackType: this.carTrackSettings.track.type,
      track: this.carTrackSettings.track.name,
      frontTires: this.carTrackSettings.frontTires,
      rearTires: this.carTrackSettings.rearTires,
      performancePoints: this.carTrackSettings.performancePoints,
      tireSize: this.carTrackSettings.tireSize,

      // Suspension settings
      rideHeightFront: this.suspensionSettings.rideHeightFront,
      rideHeightRear: this.suspensionSettings.rideHeightRear,
      downforceFront: this.suspensionSettings.downforceFront,
      downforceRear: this.suspensionSettings.downforceRear,
      weightDistributionPercent: this.suspensionSettings.weightDistributionPercent,
      oversteerUndersteer: this.suspensionSettings.oversteerUndersteer,
      arbStiffness: this.suspensionSettings.arbStiffness,

      // Complete Drivetrain settings including individual gear ratios
      drivetrainSettings: {
        maxPower: this.drivetrainSettings.maxPower,
        maxTorque: this.drivetrainSettings.maxTorque,
        maxRPM: this.drivetrainSettings.maxRPM,
        topSpeed: this.drivetrainSettings.topSpeed,
        gearCount: this.drivetrainSettings.gearCount,
        lsdSetting: this.drivetrainSettings.lsdSetting,

        // Individual gear ratios (CRITICAL for gear-related questions)
        finalGear: this.drivetrainSettings.finalGear,
        gearRatios: this.drivetrainSettings.gearRatios || {},
        gear1: this.drivetrainSettings.gearRatios?.['1'] || 0,
        gear2: this.drivetrainSettings.gearRatios?.['2'] || 0,
        gear3: this.drivetrainSettings.gearRatios?.['3'] || 0,
        gear4: this.drivetrainSettings.gearRatios?.['4'] || 0,
        gear5: this.drivetrainSettings.gearRatios?.['5'] || 0,
        gear6: this.drivetrainSettings.gearRatios?.['6'] || 0,
        gear7: this.drivetrainSettings.gearRatios?.['7'] || 0,
        gear8: this.drivetrainSettings.gearRatios?.['8'] || 0,

        // Available drivetrain properties only
        torqueDistribution: this.drivetrainSettings.torqueDistribution,
        finalGearOption: this.drivetrainSettings.finalGearOption,
        gearRatiosOption: this.drivetrainSettings.gearRatiosOption,
        minCornerSpeed: this.drivetrainSettings.minCornerSpeed,
        minCornerSpeedGear: this.drivetrainSettings.minCornerSpeedGear,
        ratioAdjuster: this.drivetrainSettings.ratioAdjuster,
        engineData: this.drivetrainSettings.engineData || []
      },

      // Complete Suspension settings (using actual SuspensionSettings properties)
      suspensionSettings: {
        weightDistributionPercent: this.suspensionSettings.weightDistributionPercent,
        lowSpeedStability: this.suspensionSettings.lowSpeedStability,
        highSpeedStability: this.suspensionSettings.highSpeedStability,
        rotationalG1: this.suspensionSettings.rotationalG1,
        rotationalG2: this.suspensionSettings.rotationalG2,
        rotationalG3: this.suspensionSettings.rotationalG3,
        rideHeightFront: this.suspensionSettings.rideHeightFront,
        rideHeightRear: this.suspensionSettings.rideHeightRear,
        downforceFront: this.suspensionSettings.downforceFront,
        downforceRear: this.suspensionSettings.downforceRear,
        calculateRearDownforce: this.suspensionSettings.calculateRearDownforce,
        springStiffness: this.suspensionSettings.springStiffness,
        arbStiffness: this.suspensionSettings.arbStiffness,
        tireWear: this.suspensionSettings.tireWear,
        oversteerUndersteer: this.suspensionSettings.oversteerUndersteer,
        reduceCornerEntryUndersteerOversteer: this.suspensionSettings.reduceCornerEntryUndersteerOversteer,
        reduceCornerExitUndersteerOversteer: this.suspensionSettings.reduceCornerExitUndersteerOversteer,
        offsetSpringFrequency: this.suspensionSettings.offsetSpringFrequency
      },

      // Current results
      suspension: {
        ...this.suspension,
        naturalFrequencyFront: this.suspension.springRateFront,
        naturalFrequencyRear: this.suspension.springRateRear
      },
      drivetrain: { ...this.drivetrain },

      // Settings name
      savedSettingsName: this.savedSettingsName
    };
  }
}
